import { assignInlineVars } from "@vanilla-extract/dynamic";
import { PropsWithChildren, Suspense } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Footer } from "chameleon/lib/components/footer/footer";

import CartItems from "checkout-experience/components/cart-items/cart-items";
import ContactInfo from "checkout-experience/components/contact-info/contact-info";
import ErrorBlock from "checkout-experience/components/error-block/error-block";
import OrderSummarySection from "checkout-experience/components/hpp-container/order-summary-section";
import OrderSummary from "checkout-experience/components/order-summary/order-summary";
import PaymentLinkSummary from "checkout-experience/components/payment-link-summary/payment-link-summary";
import PlaceOrder from "checkout-experience/components/place-order/place-order";
import ShippingAddress from "checkout-experience/components/shipping-address/shipping-address";
import ShippingMethod from "checkout-experience/components/shipping-method/shipping-method";
import { useIsCreditCardWithSurchargeChex } from "checkout-experience/hooks/use-is-credit-card-with-surcharge-chex";
import { useSurcharge } from "checkout-experience/hooks/use-surcharge";
import {
  blockContainer,
  card,
  container,
  cookiePolicyButtonStyle,
  hppLinkContainer,
  templateAreasDesktop,
  templateAreasMobile,
  templateColumnsDesktop,
  templateColumnsMobile,
} from "checkout-experience/layouts/checkout-experience.css";
import { useChexContext } from "checkout-experience/providers/chex-context-wrapper";
import { getMerchantLayoutVars } from "checkout-experience/utils/get-merchant-layout";
import { PaymentDomain } from "checkout-query-client/api/types";
import { FormErrorCode } from "checkout-query-client/types/form-types";

import { PayRequestErrorCode } from "frontend-client";

import { getIsFeatureEnabled } from "splitio-loader/src/utils/get-split-io-flag";

interface Props extends PropsWithChildren {
  paymentError?: PayRequestErrorCode;
  showPlaceOrderSection: boolean;
  onSubmit: () => Promise<void>;
  applePayComponent: JSX.Element | null;
  isPayInProgress: boolean;
  paymentDomain?: PaymentDomain;
}

// Component to present layout for HPP and PBL
const HppContainer = ({
  paymentError,
  children,
  showPlaceOrderSection,
  onSubmit,
  applePayComponent,
  isPayInProgress,
  paymentDomain,
}: Props) => {
  const { t } = useTranslation();
  const { merchantSettings, experienceType, target } = useChexContext();
  const merchantLayoutVars = getMerchantLayoutVars();
  const isPblEnhancementsEnabledFlag = getIsFeatureEnabled(
    target,
    "q1-2025-pbl-enhancements"
  );
  const checkoutForm = useFormContext();
  const { isError: isCreditCardError } =
    useIsCreditCardWithSurchargeChex(checkoutForm);
  const surcharge = useSurcharge();

  let containerCSS = container;

  if (experienceType === "hppLink") {
    containerCSS = container + " " + hppLinkContainer;
  }

  return (
    <Suspense fallback={t("loading")}>
      <main data-testid="hpp-container" className={containerCSS}>
        <div
          className={blockContainer}
          style={assignInlineVars({
            [templateAreasDesktop]: merchantLayoutVars.over1024.templateAreas,
            [templateAreasMobile]: merchantLayoutVars.default.templateAreas,
            [templateColumnsDesktop]:
              merchantLayoutVars.over1024.templateColumns,
            [templateColumnsMobile]: merchantLayoutVars.default.templateColumns,
          })}
        >
          <div>
            <ErrorBlock
              paymentError={paymentError}
              formError={
                isCreditCardError ? FormErrorCode.cardTypeError : undefined
              }
            />
            {experienceType !== "hppLink" &&
              merchantSettings?.ordersOnHpp?.displayContactInfo && (
                <section
                  data-testid="contact-section"
                  aria-labelledby="contactSectionHeaderId"
                  className={card}
                >
                  <ContactInfo />
                </section>
              )}
            {experienceType !== "hppLink" &&
              merchantSettings?.ordersOnHpp?.displayShippingDetails && (
                <section
                  data-testid="shipping-address-section"
                  aria-labelledby="shippingAddressSectionHeaderId"
                  className={card}
                >
                  <ShippingAddress />
                </section>
              )}
            {experienceType !== "hppLink" &&
              merchantSettings?.ordersOnHpp?.displayShippingDetails && (
                <section
                  data-testid="shipping-method-section"
                  className={card}
                  aria-labelledby="shippingMethodSectionHeaderId"
                >
                  <span id="shippingMethodSectionHeaderId" hidden>
                    {t("shippingMethod.title")}
                  </span>
                  <ShippingMethod />
                </section>
              )}
            <>
              <section aria-labelledby="paymentMethodSectionHeaderId">
                {children}
              </section>
            </>
          </div>
          <div>
            {isPblEnhancementsEnabledFlag ? (
              <>
                {(experienceType === "hppLink" ||
                  merchantSettings?.ordersOnHpp?.displayCartItems) && (
                  <section
                    data-testid="cart-items-section"
                    aria-labelledby="cartItemsSectionHeaderId"
                    className={card}
                  >
                    <CartItems hideImages={experienceType === "hppLink"} />
                  </section>
                )}
                {merchantSettings?.ordersOnHpp?.displayOrderSummary ||
                experienceType === "hppLink" ? (
                  <OrderSummarySection>
                    <OrderSummary surcharge={surcharge} />
                  </OrderSummarySection>
                ) : null}
              </>
            ) : (
              <>
                {experienceType !== "hppLink" &&
                  merchantSettings?.ordersOnHpp?.displayCartItems && (
                    <section
                      data-testid="cart-items-section"
                      aria-labelledby="cartItemsSectionHeaderId"
                      className={card}
                    >
                      <CartItems />
                    </section>
                  )}
                {experienceType === "hppLink" ? (
                  <OrderSummarySection>
                    <PaymentLinkSummary surcharge={surcharge} />
                  </OrderSummarySection>
                ) : merchantSettings?.ordersOnHpp?.displayOrderSummary ? (
                  <OrderSummarySection>
                    <OrderSummary surcharge={surcharge} />
                  </OrderSummarySection>
                ) : null}
              </>
            )}
            {showPlaceOrderSection ? (
              <section
                data-testid="place-order-section"
                aria-labelledby="placeOrderSectionHeaderId"
                className={card}
              >
                {/** Global pay button for HPP */}
                <PlaceOrder
                  showAction={showPlaceOrderSection}
                  onClick={onSubmit}
                  applePayComponent={applePayComponent}
                  isPayInProgress={isPayInProgress}
                  paymentDomain={paymentDomain}
                />
              </section>
            ) : null}
          </div>
        </div>
      </main>
      {experienceType === "hppLink" && (
        <Footer>
          <button
            id="ot-sdk-btn"
            className={`ot-sdk-show-settings ` + cookiePolicyButtonStyle}
          >
            {t("cookieSettings")}
          </button>
        </Footer>
      )}
    </Suspense>
  );
};
export default HppContainer;
